// @flow
import type { Node } from 'react';

import React from 'react';
import { InputGroup } from 'reactstrap';
import BaseField from './BaseField/index.js';
import Label from './EnhancedLabel/index.js';
import InputGroupAddon from './EnhancedInputGroupAddon/index.js';
import getFullyQualifiedFieldName from '#support/utility/form/getFullyQualifiedFieldName.js';
import FieldFeedback from './FieldFeedback/index.js';

type Props = {
  excludeLabel?: boolean,
  excludeFeedback?: boolean,
  excludeTip?: boolean,
  excludeInputGroup?: boolean,
  field: Object,
  item?: string | null,
  render?: Function,
  visible: boolean,
  readOnly?: boolean,
  readOnlyComponent?: any,
  validation?: Object,
  autofilled?: boolean,
  customWrapper?: ?(labelElement: Node, fieldElement: Node, feedbackElement: Node) => Node,
};

const EnhancedField = ({
  excludeLabel = false,
  excludeFeedback = false,
  excludeTip = false,
  excludeInputGroup = false,
  field,
  item = null,
  render = null,
  visible,
  readOnly = false,
  readOnlyComponent = null,
  validation = null,
  autofilled = false,
  customWrapper = null,
}: Props): Node => {
  if (!visible) {
    return null;
  }

  const fullyQualifiedName = getFullyQualifiedFieldName(item, field.name);
  const excludeIG = excludeInputGroup || field.excludeInputGroup;
  const excludeT = excludeTip || field.excludeTip;
  const isCheckbox = field.type === 'checkbox';

  // Base field props (shared subset)
  const baseFieldProps = {
    form: field.form,
    name: fullyQualifiedName,
    type: field.type,
    options: field.options,
    format: field.format,
    normalize: field.normalize,
    item,
    readOnly,
    ...(readOnlyComponent && { readOnlyComponent }),
    ...(validation && { validation }),
    ...(autofilled !== false && { autofilled }),
    ...(render && { render }),
  };

  // Label element - different handling for checkboxes
  const labelElement =
    excludeLabel || field.excludeLabel || !field.label ? null : (
      <Label excludeTip={excludeT} field={field} htmlFor={fullyQualifiedName} check={isCheckbox} />
    );

  // Field element with InputGroup support
  const fieldElement =
    excludeIG || (!field.prefix && !field.suffix) ? (
      /* eslint-disable react/jsx-props-no-spreading */
      <BaseField {...baseFieldProps} />
    ) : (
      <InputGroup>
        {field.prefix && <InputGroupAddon field={field} addonType="prepend" />}
        <BaseField {...baseFieldProps} />
        {field.suffix && <InputGroupAddon field={field} addonType="append" />}
      </InputGroup>
    );
  /* eslint-enable react/jsx-props-no-spreading */

  // Feedback element
  const feedbackElement =
    excludeFeedback || field.feedback === false ? null : (
      <FieldFeedback form={field.form} name={fullyQualifiedName} />
    );

  // Use custom wrapper if provided, otherwise default layout
  if (customWrapper) {
    return customWrapper(labelElement, fieldElement, feedbackElement);
  }

  // Checkbox layout
  if (isCheckbox) {
    return (
      <div className="form-check">
        {fieldElement}
        {labelElement}
        {feedbackElement}
      </div>
    );
  }

  // Default layout for non-checkbox fields
  return (
    <>
      {labelElement}
      {fieldElement}
      {feedbackElement}
    </>
  );
};

export default EnhancedField;
